import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import type { ErrorComponentProps } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { BookMarked, ArrowRight, Clock, PlayCircle, Instagram } from "lucide-react";
import { getCollectionBySlug, type CollectionDetail } from "@/lib/content.functions";
import { resolveImageWithFallback } from "@/lib/asset-map";
import { SmartImage } from "@/components/SmartImage";
import { usePageView } from "@/hooks/use-page-view";
import { ShareButtons } from "@/components/ShareButtons";
import { useCollectionProgress } from "@/hooks/use-collection-progress";
import { getJourney } from "@/lib/journeys";
import { SeriesSeasons } from "@/components/seasons/SeriesSeasons";
import { retiredCollectionTarget } from "@/lib/retired-collections";
import { BackButton } from "@/components/BackButton";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { useLocale } from "@/hooks/use-locale";
import { countCopy, listCopy, localizedSeriesEyebrow, seriesSummary, seriesTitle } from "@/lib/list-locales";


const READ_MIN_PER = 4;

export const Route = createFileRoute("/collections/$slug")({
  loader: async ({ params, context }) => {
    const data = await context.queryClient.ensureQueryData({
      queryKey: ["collection", params.slug],
      queryFn: () => getCollectionBySlug({ data: { slug: params.slug } }),
    });
    if (!data) {
      // Retired collections still hold Google rankings - hand the traffic to
      // the surviving collection instead of burning it on a 404.
      const target = retiredCollectionTarget(params.slug);
      if (target) {
        throw redirect({ to: "/collections/$slug", params: { slug: target }, statusCode: 301 });
      }
      throw redirect({ to: "/collections", statusCode: 301 });
    }
    return data;
  },

  head: ({ loaderData }) => {
    const d = loaderData as CollectionDetail | undefined;
    const title = d ? `${d.collection.title} - Smartasaurus` : "Collection - Smartasaurus";
    const desc =
      d?.collection.description ??
      `A curated rabbit hole of ${d?.items.length ?? ""} discoveries. Explore the full collection on Smartasaurus.`;
    const url = d ? `https://smartasaurus.com/collections/${d.collection.slug}` : "https://smartasaurus.com/collections";
    const img = d?.collection.cover_url ?? d?.items[0]?.image_url ?? undefined;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
        ...(img ? [{ property: "og:image", content: img }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(img ? [{ name: "twitter:image", content: img }] : []),
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: CollectionDetailPage,
  notFoundComponent: () => (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-4 py-24 text-center">
        <h1 className="font-display text-3xl uppercase">Collection not found</h1>
        <Link to="/collections" className="mt-6 inline-flex items-center gap-2 text-primary">
          See all collections <ArrowRight className="h-4 w-4" />
        </Link>
      </main>
      <SiteFooter />
    </div>
  ),
  errorComponent: ({ error }: ErrorComponentProps) => (
    <div className="p-8 text-center text-muted-foreground">{(error as Error).message}</div>
  ),
});

function CollectionDetailPage() {
  const { collection, items } = Route.useLoaderData() as CollectionDetail;
  const [lang] = useLocale();
  const copy = listCopy(lang);
  const counts = countCopy(lang);
  const localTitle = seriesTitle(lang, collection.title);
  const localDescription = seriesSummary(lang, collection.slug, collection.description ?? "");
  usePageView("collection");
  const { visited } = useCollectionProgress(collection.slug);
  const minutes = items.length * READ_MIN_PER;
  const cover = resolveImageWithFallback(collection.cover_url ?? items[0]?.image_url ?? null, "collection", collection.slug);
  const visitedCount = items.filter((i) => visited.has(i.slug)).length;
  const pct = items.length > 0 ? Math.round((visitedCount / items.length) * 100) : 0;
  const nextUp = items.find((i) => !visited.has(i.slug)) ?? items[0];
  // For Journey collections, tag every outbound discovery link with the
  // collection slug so multi-collection discoveries resolve back into THIS
  // Journey (correct Discovery X of Y, Prev/Next, completion).
  const journeySearch = getJourney(collection.slug) ? ({ from: collection.slug } as const) : undefined;

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main-content">
        <section className="relative border-b border-border">
          <div className="relative h-[46vh] min-h-[320px] max-h-[520px] w-full overflow-hidden md:h-[52vh] md:max-h-[560px]">
            {cover ? (
              <SmartImage src={cover} alt={localTitle} className="absolute inset-0 h-full w-full object-cover" />
            ) : (
              <div className="absolute inset-0 bg-gradient-curiosity opacity-50" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/20" />
          </div>
          <div className="relative z-10 mx-auto -mt-24 max-w-5xl px-4 pb-8 md:-mt-28 md:px-8 md:pb-10">
            <div className="mb-4 flex flex-wrap items-center gap-3">
              <BackButton fallback={{ to: "/collections" }} />
              <Breadcrumbs
                items={[
                  { label: copy.series, to: "/collections" },
                  { label: localTitle, current: true },
                ]}
              />
            </div>
            <div data-no-translate className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-[10px] font-bold uppercase leading-relaxed tracking-[0.2em] text-primary sm:tracking-[0.3em]">
              <BookMarked className="h-3.5 w-3.5 shrink-0" /> <span className="break-words">{localizedSeriesEyebrow(lang, getJourney(collection.slug)?.eyebrow ?? "Series")}</span>
            </div>
            <h1 data-no-translate className="mt-2 break-words font-display text-4xl uppercase leading-[0.95] [text-wrap:balance] md:text-6xl">{localTitle}</h1>
            {localDescription && (
              <p data-no-translate className="mt-4 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">{localDescription}</p>
            )}
            <div data-no-translate className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-2 text-[11px] uppercase leading-relaxed tracking-wider text-muted-foreground sm:tracking-widest">
              <span>{counts.episodes(items.length)}</span>
              <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {counts.minutes(minutes)}</span>
              {visitedCount > 0 && <span>{copy.percentComplete(pct)}</span>}
            </div>
            {nextUp && (
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <Link
                  to="/discovery/$slug"
                  params={{ slug: nextUp.slug }}
                  search={getJourney(collection.slug) ? { from: collection.slug } : undefined}
                  className="inline-flex min-h-11 max-w-full items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase leading-snug tracking-wider text-primary-foreground transition hover:brightness-110"
                >
                  <PlayCircle className="h-4 w-4" />
                  {visitedCount === 0
                    ? copy.startSeries
                    : visitedCount === items.length
                      ? copy.revisit
                      : copy.continueSeries}
                  <ArrowRight className="h-4 w-4" />
                </Link>
                <ShareButtons url={`https://smartasaurus.com/collections/${collection.slug}`} title={collection.title} compact />
              </div>
            )}
          </div>
        </section>

        {collection.slug === "ocean-deep" && (
          <section className="mx-auto max-w-5xl px-4 pt-8 md:px-8">
            <Link
              to="/descent"
              className="group flex items-center gap-4 rounded-2xl border border-sky-500/30 bg-gradient-to-r from-sky-950 to-slate-950 p-5 transition hover:border-sky-400/60"
            >
              <div className="min-w-0 flex-1">
                <div data-no-translate className="text-[10px] font-bold uppercase tracking-[0.3em] text-sky-400">{copy.interactive}</div>
                <h2 className="mt-1 font-display text-xl uppercase leading-tight text-white sm:text-2xl">
                  <span data-no-translate>{copy.descentTitle}</span>
                </h2>
                <p data-no-translate className="mt-1 text-sm leading-relaxed text-white/70">
                  {copy.descentCopy}
                </p>
              </div>
              <ArrowRight className="h-5 w-5 shrink-0 text-sky-400 transition group-hover:translate-x-1" />
            </Link>
          </section>
        )}

        {(() => {
          const igItems = items.filter((i) => i.from_instagram).slice(0, 4);
          // Only render the reel bridge when we have enough to justify the row.
          if (igItems.length < 2) return null;
          return (
            <section className="mx-auto max-w-5xl px-4 pt-8 md:px-8">
              <div className="flex items-end justify-between gap-3">
                <div className="min-w-0">
                  <div data-no-translate className="inline-flex items-center gap-2 text-[10px] font-bold uppercase leading-relaxed tracking-[0.2em] text-pink-400 sm:tracking-[0.3em]">
                    <Instagram className="h-3.5 w-3.5 shrink-0" /> {copy.popularOnInstagram}
                  </div>
                  <p data-no-translate className="mt-1 text-xs leading-relaxed text-muted-foreground md:text-sm">
                    {copy.instagramIntro}
                  </p>
                </div>
                <span data-no-translate className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{copy.reels(igItems.length)}</span>
              </div>
              <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                {igItems.map((d) => {
                  const img = resolveImageWithFallback(d.image_url, "discovery", d.slug);
                  return (
                    <li key={d.id}>
                      <Link
                        to="/discovery/$slug"
                        params={{ slug: d.slug }}
                        search={journeySearch}
                        className="group block overflow-hidden rounded-xl border border-border/60 bg-background/60 transition hover:border-pink-500/50"
                      >
                        <div className="relative aspect-[4/5] overflow-hidden">
                          {img ? (
                            <SmartImage src={img} alt={d.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                          ) : (
                            <div className="h-full w-full bg-gradient-curiosity opacity-40" />
                          )}
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent p-2.5">
                            <h3 className="line-clamp-2 font-display text-[11px] uppercase leading-tight text-white group-hover:text-primary md:text-xs">
                              {d.title}
                            </h3>
                          </div>
                          <div className="absolute right-1.5 top-1.5 inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-pink-500 to-orange-500 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
                            <Instagram className="h-2.5 w-2.5" />
                          </div>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })()}

        <section className="mx-auto max-w-5xl px-4 py-12 md:px-8 md:py-16">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <h2 data-no-translate className="font-display text-2xl uppercase md:text-3xl">{copy.allEpisodes}</h2>
            <Link to="/collections" className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground hover:text-primary">
              <span data-no-translate>{copy.allSeries} →</span>
            </Link>
          </div>
          <SeriesSeasons items={items} visited={visited} search={journeySearch} seriesSlug={collection.slug} />

        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
